import React from 'react';
import { calculateSunnyScore, getSunnyScoreCategory, getSunnyScoreEmoji } from './SunnyScore';

/**
 * Komponente zur Anzeige der 14-Tage-Wettervorhersage als Tabelle
 * @param {Object} props - Component props
 * @param {Array} props.forecastData - Daten für die Wettervorhersage
 * @returns {JSX.Element} ForecastTable-Komponente
 */
const ForecastTable = ({ forecastData }) => {
  if (!forecastData || forecastData.length === 0) {
    return null;
  }

  // Formatierung des Datums
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.`;
  };

  // Bestimmen der Hintergrundfarbe basierend auf dem Score
  const getRowColor = (score) => {
    if (score >= 70) return 'bg-green-50 border-green-200'; // Geändert von blue zu green für "Sehr gut"
    if (score >= 55) return 'bg-amber-50 border-amber-200';
    return 'bg-rose-50 border-rose-200';
  };

  return (
    <div className="mt-8 mb-8">
      <div className="flex items-center mb-4">
        <span className="text-xl mr-3" role="img" aria-label="forecast">📊</span>
        <h2 className="text-xl font-semibold text-gray-800">14-Tage-Vorhersage</h2>
      </div>
      
      {/* Hier wurde der Container angepasst, um einen Schatten und runde Ecken hinzuzufügen */}
      <div className="overflow-x-auto rounded-xl shadow-sm border border-gray-200">
        <table className="min-w-full bg-white">
          <thead className="bg-blue-100 text-blue-600 border-b border-blue-200"> 
            <tr>
              <th className="py-3 px-4 text-left font-medium rounded-tl-xl">Datum</th>
              <th className="py-3 px-4 text-center font-medium">SunnyScore</th>
              <th className="py-3 px-4 text-center font-medium">Bewertung</th>
              <th className="py-3 px-4 text-center font-medium">Temperatur</th>
              <th className="py-3 px-4 text-center font-medium">Sonnenstunden</th>
              <th className="py-3 px-4 text-center font-medium rounded-tr-xl">Regenwahrscheinlichkeit</th>
            </tr>
          </thead>
          <tbody>
            {forecastData.map((day, index) => {
              const score = calculateSunnyScore(
                day.sonneneinstrahlung,
                day.niederschlag,
                day.luftfeuchtigkeit,
                day.windgeschwindigkeit,
                new Date(day.datum).getMonth() + 1
              );
              
              const category = getSunnyScoreCategory(score);
              const emoji = getSunnyScoreEmoji(score);
              const rowColor = getRowColor(score);
              
              // Füge abgerundete Ecken für die letzte Zeile hinzu
              const isLastRow = index === forecastData.length - 1;
              
              return (
                <tr key={index} className={`border-t ${rowColor}`}>
                  <td className={`py-3 px-4 ${isLastRow ? 'rounded-bl-xl' : ''}`}>
                    <div className="font-medium">{formatDate(day.datum)}</div>
                    <div className="text-sm text-gray-600">{day.wochentag}</div>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <div className="inline-flex items-center justify-center">
                      <span className="text-lg font-bold">{score}</span>
                      <span className="ml-1">{emoji}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-center">{category}</td>
                  <td className="py-3 px-4 text-center">{Math.round(day.temperatur)}°C</td>
                  <td className="py-3 px-4 text-center">{day.sonnenstunden}h</td>
                  <td className={`py-3 px-4 text-center ${isLastRow ? 'rounded-br-xl' : ''}`}>
                    {Math.round(day.regenwahrscheinlichkeit)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ForecastTable;